import { message } from 'antd';
import { action, computed, observable, runInAction, reaction, autorun } from 'mobx';
import { ProjectsRootVisualStore, ProjectsStore, RouterStore, UsersStore } from '../../common/stores';
import { PAGE_SIZE } from '../../common/stores/Pager';
import { PackageChange, SearchPackagesRequest, TestProjectChange, OrderBy } from '../../common/types';
import { TestProjectService } from '../services';
import { TestProjectListModel } from '../types';
import { Package } from '../../common/models';
import { ProjectsService, LocalStorageService } from '../../common/services';
import { TestProjectsNavigation } from '../routes';
import { Subject } from 'rxjs';
import { TestProjectFilterValue } from '../components/TestProjectFilter';
import { BaselineStatusFilterValue } from '../components/TestProjectsList/BaselineStatusFilter';
import { NO_TAGS_VALUE } from '../components/TestProjectsList/TestProjectTagsFilter';
import { BaselineCoverageCategory, TestProjectUtils } from '../misc';

const testProjectFilterStorageVersion = 1;

type TestProjectFilterStorageValues = {
    searchValue: string;
    filterValue: TestProjectFilterValue;
    selectedTagsFilter: string[];
    minDocCount: number | undefined;
    baselineStatusFilterValue: BaselineStatusFilterValue;
    orderBy: OrderBy | undefined;
};

const defaultFilterValues = {
    searchValue: '',
    filterValue: TestProjectFilterValue.ALL,
    selectedTagsFilter: [] as string[],
    minDocCount: undefined,
    baselineStatusFilterValue: BaselineStatusFilterValue.ALL,
    orderBy: { field: 'createdTime', direction: 'DESC' } as OrderBy
} as const;

type TestProjectFilterStorageItem = Partial<TestProjectFilterStorageValues>;

export default class TestProjectsVisualStore {
    @observable
    testProjects: TestProjectListModel[] = [];

    @observable
    isLoadingProjects: boolean = false;

    @observable
    searchValue: string = '';

    @observable
    filterValue: TestProjectFilterValue = TestProjectFilterValue.ALL;

    @observable
    selectedTagsFilter: string[] = [];

    @observable
    minDocCount: number | undefined = undefined;

    @observable
    baselineStatusFilterValue: BaselineStatusFilterValue = BaselineStatusFilterValue.ALL;

    @observable
    orderBy: OrderBy | undefined = undefined;

    @observable
    loadingPackages: boolean = false;

    @observable
    loadingPackagesProgress: number = 0;

    @observable
    loadingPackagesErrorMessage?: string;

    @observable
    packages: Package[] = [];

    @observable
    testProjectChangesSubject = new Subject<TestProjectChange>();

    @observable
    isCopyingTestProject: boolean = false;

    private readonly localStorageService = new LocalStorageService();

    private loadTestProjectsRequestId: number = 0;
    private loadTestProjectsLastAppliedId: number = 0;

    @computed
    get currentProject() {
        return this.projectsStore.currentProject;
    }

    @computed
    get filteredProjects() {
        let projects = this.testProjects.filter(project =>
            project.name.toLowerCase().includes(this.searchValue.toLowerCase())
        );

        if (this.selectedTagsFilter.length > 0) {
            projects = projects.filter(project => {
                const hasNoTagsFilter = this.selectedTagsFilter.includes(NO_TAGS_VALUE);
                const hasOtherTagFilters = this.selectedTagsFilter.some(tag => tag !== NO_TAGS_VALUE);

                if (!project.userTags || project.userTags.length === 0) {
                    return hasNoTagsFilter;
                }

                if (hasOtherTagFilters) {
                    const hasMatchingTags = this.selectedTagsFilter
                        .filter(tag => tag !== NO_TAGS_VALUE)
                        .some(selectedTag => project.userTags!.includes(selectedTag));

                    return hasMatchingTags;
                }

                return false;
            });
        }

        projects = projects.filter(project => {
            if (this.filterValue === TestProjectFilterValue.ALL) {
                return true;
            }

            const lastRunFuzzy = project.lastRunFuzzy != null ? project.lastRunFuzzy : null;

            if (lastRunFuzzy === null) {
                return false;
            }

            switch (this.filterValue) {
                case TestProjectFilterValue.HIGH_PERFORMING:
                    return lastRunFuzzy >= 90;
                case TestProjectFilterValue.NEEDS_IMPROVEMENT:
                    return lastRunFuzzy >= 80 && lastRunFuzzy < 90;
                case TestProjectFilterValue.REQUIRES_REVIEW:
                    return lastRunFuzzy < 80 && project.lastRunError == null;
                default:
                    return true;
            }
        });
        projects = projects.filter(project => {
            if (this.baselineStatusFilterValue === BaselineStatusFilterValue.ALL) {
                return true;
            }

            const baselineCategory = TestProjectUtils.getBaselineCoverageCategory(project.baselineCoverage);

            switch (this.baselineStatusFilterValue) {
                case BaselineStatusFilterValue.HIGH_COVERAGE:
                    return baselineCategory === BaselineCoverageCategory.HIGH_COVERAGE;
                case BaselineStatusFilterValue.LOW_COVERAGE:
                    return baselineCategory === BaselineCoverageCategory.LOW_COVERAGE;
                case BaselineStatusFilterValue.EMPTY_BASELINES:
                    return baselineCategory === BaselineCoverageCategory.EMPTY_BASELINES;
                default:
                    return true;
            }
        });
        if (this.minDocCount != null && this.minDocCount > 0) {
            projects = projects.filter(project => {
                return project.lastRunDocCount != null && project.lastRunDocCount >= this.minDocCount!;
            });
        }

        return projects;
    }

    @computed
    get users() {
        return this.usersStore.users;
    }

    @computed
    get isLoadingUsers() {
        return this.usersStore.isLoading;
    }

    @computed
    get availableTestProjectTagsWithCounts() {
        const tagCounts: Record<string, number> = {};

        this.testProjects.forEach(project => {
            if (project.userTags && project.userTags.length > 0) {
                project.userTags.forEach(tag => {
                    tagCounts[tag] = (tagCounts[tag] || 0) + 1;
                });
            }
        });

        return Object.entries(tagCounts)
            .map(([tag, count]) => ({ tag, count }))
            .sort((a, b) => a.tag.localeCompare(b.tag));
    }

    constructor(
        private service: TestProjectService,
        private projectsStore: ProjectsRootVisualStore,
        private projectsService: ProjectsService,
        private routerStore: RouterStore,
        private projectsBaseStore: ProjectsStore,
        private usersStore: UsersStore
    ) {
        this.projectsBaseStore.testProjectChanges.subscribe(testProjectChanges => {
            this.handleTestProjectChanges(testProjectChanges);
            this.testProjectChangesSubject.next(testProjectChanges);
        });

        this.projectsBaseStore.packageChanges.subscribe(this.handlePackageChanges);

        reaction(
            () => this.currentProject,
            () => {
                if (!this.currentProject) {
                    return;
                }

                const storedFilters = this.localStorageService.getItem<TestProjectFilterStorageItem>(
                    this.getTestProjectFilterStorageKey(this.currentProject.id),
                    {}
                );

                runInAction(() => {
                    this.searchValue = storedFilters.searchValue ?? defaultFilterValues.searchValue;
                    this.filterValue = storedFilters.filterValue ?? defaultFilterValues.filterValue;
                    this.selectedTagsFilter =
                        storedFilters.selectedTagsFilter ?? defaultFilterValues.selectedTagsFilter;
                    this.minDocCount = storedFilters.minDocCount ?? defaultFilterValues.minDocCount;
                    this.baselineStatusFilterValue =
                        storedFilters.baselineStatusFilterValue ?? defaultFilterValues.baselineStatusFilterValue;
                    this.orderBy = storedFilters ? storedFilters.orderBy : defaultFilterValues.orderBy;
                });
            }
        );

        autorun(() => {
            if (!this.currentProject) {
                return;
            }

            this.localStorageService.setItem<TestProjectFilterStorageItem>(
                this.getTestProjectFilterStorageKey(this.currentProject.id),
                {
                    searchValue: this.searchValue,
                    filterValue: this.filterValue,
                    selectedTagsFilter: this.selectedTagsFilter,
                    minDocCount: this.minDocCount,
                    baselineStatusFilterValue: this.baselineStatusFilterValue,
                    orderBy: this.orderBy
                }
            );
        });
    }

    @action.bound
    handlePackageChanges(packageChanges: PackageChange) {
        if (packageChanges.projectId !== this.currentProject?.id) {
            return;
        }

        const indexOfPackage = this.packages.findIndex(p => p.id === packageChanges.id);
        if (indexOfPackage === -1) {
            return;
        }

        const newPackages = [...this.packages];

        newPackages[indexOfPackage].serverState = packageChanges.state;
        runInAction(() => {
            this.packages = newPackages;
        });
    }

    @action.bound
    handleTestProjectChanges(tpChanges: TestProjectChange) {
        if (tpChanges.projectId !== this.currentProject?.id) {
            return;
        }

        const testProjectToUpdate = this.testProjects.find(tp => tp.id === tpChanges.id);
        const index = this.testProjects.indexOf(testProjectToUpdate!);

        if (testProjectToUpdate?.isAborting && tpChanges.isProgressUpdate) {
            return;
        }

        if (index < 0) {
            return;
        }

        runInAction(() => {
            const newTestProjects = [...this.testProjects];
            newTestProjects[index].isRunning = tpChanges.isRunning;
            newTestProjects[index].lastRunId = tpChanges.lastRunId;
            newTestProjects[index].lastRunFuzzy = tpChanges.lastRunFuzzy;
            newTestProjects[index].lastRunTime =
                new Date(tpChanges.lastRunTime as string).getTime() > Date.now()
                    ? new Date(Date.now()).toISOString()
                    : tpChanges.lastRunTime;
            newTestProjects[index].lastRunError = tpChanges.lastRunError;
            newTestProjects[index].lastRunDocCount = tpChanges.lastRunDocCount;
            newTestProjects[index].processedPackageCount = tpChanges.processedPackageCount;
            newTestProjects[index].avgTopicProcessingTime = tpChanges.avgTopicProcessingTime;
            newTestProjects[index].isAborting = tpChanges.isAborting;
            this.testProjects = newTestProjects;
        });

        if (tpChanges.isRunning === false) {
            this.loadTestProjects();
        }
    }

    @action.bound
    setIsLoadingProjects(isLoading: boolean) {
        this.isLoadingProjects = isLoading;
    }

    @action.bound
    setSearchValue(value: string) {
        this.searchValue = value;
    }

    @action.bound
    setFilterValue(value: TestProjectFilterValue) {
        this.filterValue = value;
    }

    @action.bound
    setSelectedTagsFilter(tags: string[]) {
        this.selectedTagsFilter = tags;
    }

    @action.bound
    setMinDocCount(value: number | undefined) {
        this.minDocCount = value;
    }

    @action.bound
    setBaselineStatusFilterValue(value: BaselineStatusFilterValue) {
        this.baselineStatusFilterValue = value;
    }

    @action.bound
    setOrderBy(orderBy: OrderBy | undefined) {
        this.orderBy = orderBy;
    }

    @action.bound
    async loadTestProjects() {
        if (!this.currentProject) {
            return;
        }

        const requestId = ++this.loadTestProjectsRequestId;
        const projectId = this.currentProject.id;

        this.setIsLoadingProjects(true);
        try {
            const testProjects = await this.service.getTestProjects(projectId);

            // Discard if a newer response was already applied or the project changed.
            // Read directly from projectsStore to avoid stale @computed cache after await.
            const currentProjectId = this.projectsStore.currentProject?.id;
            if (requestId < this.loadTestProjectsLastAppliedId || projectId !== currentProjectId) {
                return;
            }

            this.loadTestProjectsLastAppliedId = requestId;
            this.testProjects = testProjects;
        } catch (err) {
            const currentProjectId = this.projectsStore.currentProject?.id;
            if (requestId < this.loadTestProjectsLastAppliedId || projectId !== currentProjectId) {
                return;
            }

            message.error('Failed to load test projects');
            console.error(err);
        } finally {
            const currentProjectId = this.projectsStore.currentProject?.id;
            if (requestId === this.loadTestProjectsRequestId && projectId === currentProjectId) {
                this.setIsLoadingProjects(false);
            }
        }
    }

    @action.bound
    async loadAllProjectPackages() {
        if (!this.currentProject) {
            return;
        }

        try {
            runInAction(() => {
                this.loadingPackages = true;
                this.loadingPackagesProgress = 0;
            });

            let request: SearchPackagesRequest = {
                page: 0,
                pageSize: PAGE_SIZE,
                projectId: this.currentProject.id,
                allSources: true,
                uploadedBy: this.projectsStore.hasAccessToAllEntities ? undefined : this.projectsStore.currentUserId
            };
            const firstResponse = await this.projectsService.searchPackages(this.currentProject, request);

            runInAction(() => {
                this.packages = firstResponse.lines;
            });

            if (firstResponse.lines.length < firstResponse.total) {
                const pages = Math.ceil(firstResponse.total / PAGE_SIZE);
                for (let i = 1; i < pages; i++) {
                    request.page = i;
                    const response = await this.projectsService.searchPackages(this.currentProject, request);
                    runInAction(() => {
                        this.packages = this.packages.concat(response.lines);
                        this.loadingPackagesProgress = Math.floor((i / pages) * 100);
                    });
                }
            }
        } catch (err) {
            console.error(err);
            message.error(err);
            runInAction(() => {
                this.loadingPackagesErrorMessage = err;
            });
        } finally {
            runInAction(() => {
                this.loadingPackagesProgress = 100;

                setTimeout(() => {
                    this.loadingPackages = false;
                    this.loadingPackagesErrorMessage = undefined;
                }, 1500);
            });
        }
    }

    @action.bound
    goToTestProjectDashboard(id: string) {
        if (!this.currentProject) {
            return;
        }

        this.routerStore.push(
            TestProjectsNavigation.TestProjectDashboardPage.replace(':projectId', this.currentProject.id).replace(
                ':testProjectId',
                id
            )
        );
    }

    @action.bound
    getPackageById(id: string) {
        return this.packages.find(p => p.id === id);
    }

    @action.bound
    async deleteTestProject(id: string) {
        if (!this.currentProject) {
            return;
        }

        try {
            const resp = await this.service.deleteTestProject(this.currentProject.id, id);

            if (!resp.isOk()) {
                message.error(resp.error);
                console.log(resp.error);
                return;
            }

            message.success('Test project deleted');
            await this.loadTestProjects();
        } catch (err) {
            console.error(err);
            message.error('Failed to delete test project');
        }
    }

    @action.bound
    async runTestProject(testProjectId: string) {
        if (!this.currentProject) {
            return;
        }

        try {
            const resp = await this.service.runTestProject(this.currentProject.id, testProjectId);

            if (!resp.isOk()) {
                message.error(resp.error);
                console.log(resp.error);
                return;
            }
            message.success('Test project started');
        } catch (err) {
            console.error(err);
            message.error('Failed to start test project');
        }
    }

    @action.bound
    goToTestResults(testProjectId: string, runId: string) {
        if (!this.currentProject) {
            return;
        }

        this.routerStore.push(
            TestProjectsNavigation.TestProjectRunResultsPage.replace(':projectId', this.currentProject.id)
                .replace(':testProjectId', testProjectId)
                .replace(':runId', runId)
        );
    }

    @action.bound
    setIsCopyingTestProject(isCopying: boolean) {
        this.isCopyingTestProject = isCopying;
    }

    @action.bound
    async createCopyOfTestProject(testProjectId: string) {
        if (!this.currentProject || this.isCopyingTestProject) {
            return;
        }

        try {
            this.setIsCopyingTestProject(true);
            const resp = await this.service.createCopyOfTestProject(this.currentProject.id, testProjectId);

            if (!resp.isOk()) {
                message.error('Failed to copy test project');
                console.log(resp.error);
                return;
            }

            message.success('Test project copied');
            await this.loadTestProjects();
        } catch (err) {
            console.error(err);
            message.error('Failed to copy test project');
        } finally {
            this.setIsCopyingTestProject(false);
        }
    }

    getUserNameById(id: string) {
        return this.usersStore.getUserNameById(id);
    }

    private getTestProjectFilterStorageKey(projectId: string) {
        return `test-project-filter-${projectId}-v${testProjectFilterStorageVersion}`;
    }
}
